import { marketSessionDate } from 'utils/shared/time/marketSessionDate';
import {
  etMinutesOfDay,
  isUsEquityTradingDate,
  nextUsEquitySessionDate,
} from 'utils/shared/time/usEquityCalendar';

/** Publication estimates, not the options tape's hours or a generation-completion promise. */
export const FLOW_ANALYSIS_UPDATE_MINUTES = [630, 690, 750, 810, 870, 930, 990] as const;
export interface FlowAnalysisSchedule {
  nextUpdateMs: number;
  scheduleDate: string;
}

/** An ET wall-clock time on the TARGET date; using today's offset breaks DST weekends. */
export const flowAnalysisSlotTime = (dateIso: string, minutes: number): number => {
  const [year, month, day] = dateIso.split('-').map(Number);
  const noonUtc = Date.UTC(year, month - 1, day, 12);
  const offsetMinutes = etMinutesOfDay(noonUtc) - 12 * 60;
  return Date.UTC(year, month - 1, day, 0, minutes - offsetMinutes);
};

export const flowAnalysisTimestamp = (value: string | null, nowMs: number): number | null => {
  const timestamp = value ? Date.parse(value) : NaN;
  // Do not turn missing or invalid publication metadata into an invented timestamp.
  return Number.isFinite(timestamp) && timestamp <= nowMs + 60_000 ? timestamp : null;
};

export const getFlowAnalysisSchedule = (
  nowMs: number,
  lastUpdated: string | null = null,
): FlowAnalysisSchedule | null => {
  if (!Number.isFinite(nowMs) || !Number.isFinite(new Date(nowMs).getTime())) return null;
  const publishedMs = flowAnalysisTimestamp(lastUpdated, nowMs);
  // The :30 estimate belongs to its Eastern publication hour, even if that report is early.
  // Full date/instants keep an older report from consuming a slot in the current session.
  const publishedSlotMs =
    publishedMs === null
      ? nowMs
      : flowAnalysisSlotTime(
          marketSessionDate(publishedMs),
          Math.floor(etMinutesOfDay(publishedMs) / 60) * 60 + 30,
        );
  const afterMs = Math.max(nowMs, publishedSlotMs);
  const today = marketSessionDate(nowMs);
  const slots = isUsEquityTradingDate(today)
    ? FLOW_ANALYSIS_UPDATE_MINUTES.map((minute) => flowAnalysisSlotTime(today, minute))
    : [];
  const nextToday = slots.find((slot) => slot > afterMs);
  const scheduleDate = nextToday === undefined ? nextUsEquitySessionDate(today) : today;
  return {
    nextUpdateMs: nextToday ?? flowAnalysisSlotTime(scheduleDate, FLOW_ANALYSIS_UPDATE_MINUTES[0]),
    scheduleDate,
  };
};

/** The daily cadence in the viewer's clock, including a local-midnight crossing. */
export const formatFlowAnalysisWindow = (dateIso: string, zone: string): string => {
  const start = flowAnalysisSlotTime(dateIso, FLOW_ANALYSIS_UPDATE_MINUTES[0]);
  const end = flowAnalysisSlotTime(
    dateIso,
    FLOW_ANALYSIS_UPDATE_MINUTES[FLOW_ANALYSIS_UPDATE_MINUTES.length - 1],
  );
  const options: Intl.DateTimeFormatOptions = {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  const clock = new Intl.DateTimeFormat('en-US', options);
  const clockWithZone = new Intl.DateTimeFormat('en-US', { ...options, timeZoneName: 'short' });
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: zone });
  return `${clock.format(start)}–${clockWithZone.format(end)}${
    day.format(start) === day.format(end) ? '' : ' (+1 day)'
  }`;
};

/** Dates are compared in the VIEWER'S zone, including zones whose day differs from New York. */
export const formatFlowAnalysisTime = (timestamp: number, zone: string, nowMs: number): string => {
  const dateFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: zone });
  const sameDay = dateFormatter.format(timestamp) === dateFormatter.format(nowMs);
  const sameYear = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' });
  return new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    ...(sameDay
      ? {}
      : {
          weekday: 'short' as const,
          month: 'short' as const,
          day: 'numeric' as const,
          ...(sameYear.format(timestamp) === sameYear.format(nowMs)
            ? {}
            : { year: 'numeric' as const }),
        }),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(timestamp);
};
